import { searchJobNumbersEmployees } from 'modules/settings/submodules/clients/jobNumber/components/AssignUser/store/actions';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Button } from '@material-ui/core';
import ButtonWithLoader from 'shared/components/buttons/ButtonWithLoader';
import {
    selectAssignUserJobNumberId,
    selectIsAssignUserUpdating,
} from 'modules/settings/submodules/clients/jobNumber/components/AssignUser/store/selectors';
import { setEditJobNumberId, assignUserToJobNumber } from 'modules/settings/submodules/clients/jobNumber/store/actions';
import { UserSearch } from 'modules/settings/submodules/clients/jobNumber/components/AssignUser/components/UserSearch';
import { UserSearchResults } from 'modules/settings/submodules/clients/jobNumber/components/AssignUser/components/UserSearchResults';
import { formBasicSpacing, useSettingsFormStyles } from 'modules/settings/submodules/components/sharedStyles/formStyles';
import { selectJobNumberById } from 'store/entities/configuration/configurationSelectors';
import { selectUserById } from 'store/entities/users/selectors';
import { FormikProvider, useFormik } from 'formik';
import NumberField from '../../../../../../../shared/components/formFields/NumberField';

interface IJobNumberAssignUserProps {
    onClose?: () => void;
}

export const JobNumberAssignUser = ({ onClose }: IJobNumberAssignUserProps) => {
    const classes = useSettingsFormStyles();
    const dispatch = useDispatch();
    const jobNumberId = useSelector(selectAssignUserJobNumberId);
    const jobNumber = useSelector(selectJobNumberById(jobNumberId || ''));
    const initialValues = useMemo(() => {
        return {
            avionte_placement_id: jobNumber?.avionte_placement_id,
        };
    }, [jobNumber]);

    const assignedEmployee = useSelector(selectUserById(jobNumber?.user_id || ''));
    const isLoading = useSelector(selectIsAssignUserUpdating);

    const [selectedUserId, setSelectedUserId] = useState<string | null>(assignedEmployee?.id || null);
    const selectedUser = useSelector(selectUserById(selectedUserId ?? ''));
    useEffect(() => {
        if (assignedEmployee) {
            dispatch(searchJobNumbersEmployees.success([assignedEmployee]));
        }
        return () => {
            dispatch(searchJobNumbersEmployees.success([]));
        };
    }, [dispatch, assignedEmployee]);

    const onSubmit = useCallback(values => {
        if (jobNumberId && selectedUserId) {
            const payload = {
                job_number_id: jobNumberId,
                user_id: selectedUserId,
                talent_id: selectedUser?.avionte_talent_id,
                avionte_placement_id: values.avionte_placement_id,
            };
            dispatch(assignUserToJobNumber.init(payload));
        }
    }, [dispatch, jobNumberId, selectedUser?.avionte_talent_id, selectedUserId]);

    const formikData = useFormik<{ avionte_placement_id?: string }>({
        initialValues,
        onSubmit,
        enableReinitialize: true,
    });

    const onBack = useCallback(() => {
        dispatch(setEditJobNumberId(jobNumberId));
        if (onClose) {
            onClose();
        }
    }, [dispatch, jobNumberId, onClose]);

    return (
        <FormikProvider value={formikData}>
            <form onSubmit={formikData.handleSubmit}>
                <Box
                    display="flex"
                    flexDirection="column"
                    m={1}
                    style={{ minHeight: '420px' }}
                >
                    <UserSearch />
                    <Box
                        display="flex"
                        flexGrow={1}
                        ml={2}
                        mr={2}
                    >
                        <UserSearchResults
                            selectedUserId={selectedUserId}
                            onSelectUser={setSelectedUserId}
                        />
                    </Box>
                    <Box>
                        <NumberField
                            name={'avionte_placement_id'}
                            label="Placement ID"
                            className={classes.input}
                        />
                    </Box>
                    <Box m={formBasicSpacing} mt={2}>
                        <Button
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            onClick={onBack}
                            disabled={isLoading}
                        >
                            Back
                        </Button>
                        <ButtonWithLoader
                            type="submit"
                            variant="contained"
                            color="secondary"
                            className={classes.button}
                            isLoading={isLoading}
                            data-testid="submit_update_button"
                        >
                            Assign & Save
                        </ButtonWithLoader>
                    </Box>
                </Box>
            </form>
        </FormikProvider>
    );
};
